import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { submissionAPI } from '../services/api';
import { FormattedText } from '../utils/formatText';
import { useToast } from '../components/Toast';
import Select from 'react-select';
import { setSubmissions, setFlaggedQuestions, addFlag } from '../store/submissionsSlice';
import { Skeleton } from '@mui/material';

function MyQuizzes() {
  // Get data from Redux store
  const dispatch = useDispatch();
  const { submissions, pagination, availableTags, flaggedQuestions: storedFlags, isInitialized } = useSelector(state => state.submissions);
  
  const [loading, setLoading] = useState(!isInitialized);
  const [flaggedQuestions, setLocalFlaggedQuestions] = useState(storedFlags);
  const [flaggingQuestion, setFlaggingQuestion] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'passed', 'failed'
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get current page from URL or default to 1
  const currentPage = parseInt(searchParams.get('page')) || 1;
  
  // Update local state when Redux state changes
  useEffect(() => {
    setLocalFlaggedQuestions(storedFlags);
  }, [storedFlags]);

  useEffect(() => {
    // Fetch data when mounting or when page changes
    // For page 1, always fetch to ensure we have the latest data including new submissions
    if (currentPage === 1 || !isInitialized) {
      fetchMySubmissions(currentPage);
    } else {
      // For other pages, only fetch if not already initialized
      fetchMySubmissions(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchMySubmissions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await submissionAPI.getMySubmissions(page, 10);
      
      // Extract unique tags from all quizzes
      const tagsSet = new Set();
      response.data.submissions.forEach(sub => {
        if (sub.quiz && sub.quiz.tags) {
          sub.quiz.tags.forEach(tag => {
            if (tag && tag.name) {
              tagsSet.add(tag.name);
            }
          });
        }
      });
      const tagOptions = Array.from(tagsSet).sort().map(tag => ({ value: tag, label: tag }));
      
      // Dispatch to Redux store
      dispatch(setSubmissions({
        submissions: response.data.submissions,
        pagination: response.data.pagination,
        availableTags: tagOptions
      }));
      
      // Load flags for all submissions
      loadAllFlags(response.data.submissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showToast('Failed to load submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAllFlags = async (allSubmissions) => {
    try {
      const flagsMap = {};
      await Promise.all(allSubmissions.map(async (sub) => {
        const response = await submissionAPI.getSubmissionFlags(sub._id);
        response.data.forEach(flag => {
          const key = `${sub._id}_${flag.question.toString()}`;
          flagsMap[key] = flag;
        });
      }));
      
      // Update both local state and Redux
      setLocalFlaggedQuestions(flagsMap);
      dispatch(setFlaggedQuestions(flagsMap));
    } catch (error) {
      console.error('Error loading flags:', error);
    }
  };

  const handleFlagQuestion = async (submissionId, questionId) => {
    if (!flagReason.trim()) {
      showToast('Please provide a reason for flagging this question', 'error');
      return;
    }

    try {
      await submissionAPI.flagQuestion({
        submissionId: submissionId,
        questionId: questionId,
        reason: flagReason
      });
      
      const key = `${submissionId}_${questionId}`;
      const flagData = { status: 'pending', reason: flagReason };
      
      // Update both local state and Redux
      setLocalFlaggedQuestions({
        ...flaggedQuestions,
        [key]: flagData
      });
      dispatch(addFlag({ submissionId, questionId, flag: flagData }));
      
      setFlaggingQuestion(null);
      setFlagReason('');
      showToast('Question flagged successfully. Admin will review it.', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to flag question', 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ marginBottom: '24px' }}>
            {/* Back button skeleton */}
            <Skeleton variant="rectangular" width={140} height={32} sx={{ borderRadius: '6px', marginBottom: '16px' }} />
            
            {/* Title skeleton */}
            <Skeleton variant="text" width="40%" height={32} sx={{ marginBottom: '8px' }} />
            
            {/* Description skeleton */}
            <Skeleton variant="text" width="60%" height={20} />
          </div>

          {/* Tag filter skeleton */}
          <div style={{ marginBottom: '20px' }}>
            <Skeleton variant="text" width={120} height={20} sx={{ marginBottom: '8px' }} />
            <Skeleton variant="rectangular" width="100%" height={38} sx={{ borderRadius: '6px' }} />
          </div>

          {/* Tab navigation skeleton */}
          <div style={{ 
            marginBottom: '24px',
            borderBottom: '2px solid #e2e8f0',
            display: 'flex',
            gap: '4px'
          }}>
            <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: '8px 8px 0 0' }} />
            <Skeleton variant="rectangular" width={130} height={40} sx={{ borderRadius: '8px 8px 0 0' }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '8px 8px 0 0' }} />
          </div>

          {/* Page count skeleton */}
          <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: '6px', marginBottom: '16px' }} />

          {/* Quiz cards skeleton */}
          <div style={{ display: 'grid', gap: '12px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '18px',
                  background: '#fafafa'
                }}
              >
                {/* Quiz title */}
                <Skeleton variant="text" width="70%" height={24} sx={{ marginBottom: '8px' }} />
                
                {/* Quiz description */}
                <Skeleton variant="text" width="90%" height={18} sx={{ marginBottom: '8px' }} />
                <Skeleton variant="text" width="80%" height={18} sx={{ marginBottom: '12px' }} />
                
                {/* Tags */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                  <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '10px' }} />
                  <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
                </div>
                
                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: '6px' }} />
                  <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '6px' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination skeleton */}
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <Skeleton variant="rectangular" width={60} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={60} height={36} sx={{ borderRadius: '6px' }} />
          </div>
        </div>
      </div>
    );
  }

  // Calculate counts for tabs
  // Use total count from pagination for overall count
  const allCount = pagination.totalSubmissions || Object.keys(submissions).length;
  const currentPageCount = Object.keys(submissions).length;
  const passedCount = Object.entries(submissions).filter(([_, quizSubs]) => {
    const mainSub = quizSubs[0];
    if (mainSub.quiz.showResultsToStudents === false) return false;
    const percentage = ((mainSub.score / mainSub.totalQuestions) * 100);
    return percentage >= 50;
  }).length;
  const failedCount = Object.entries(submissions).filter(([_, quizSubs]) => {
    const mainSub = quizSubs[0];
    if (mainSub.quiz.showResultsToStudents === false) return false;
    const percentage = ((mainSub.score / mainSub.totalQuestions) * 100);
    return percentage < 50;
  }).length;

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/user')}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to Dashboard
          </button>
          <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
            My Quizzes
          </h2>
          <p style={{ color: '#718096', fontSize: '13px' }}>
            View all the quizzes you have completed
          </p>
        </div>

        {/* Tag Filter */}
        {availableTags.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
              🏷️ Filter by Tags
            </label>
            <Select
              isMulti
              value={selectedTags}
              onChange={setSelectedTags}
              options={availableTags}
              placeholder="Select tags to filter..."
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: '#e2e8f0',
                  '&:hover': { borderColor: '#cbd5e0' },
                  boxShadow: 'none',
                  fontSize: '13px'
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#eef2ff',
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#667eea',
                  fontWeight: '500',
                  fontSize: '12px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#667eea',
                  '&:hover': {
                    backgroundColor: '#667eea',
                    color: 'white',
                  },
                }),
                menuPortal: (base) => ({ ...base, zIndex: 1050 })
              }}
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ 
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setActiveTab('all')}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === 'all' ? '#667eea' : '#718096',
                background: activeTab === 'all' ? '#eef2ff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'all' ? '3px solid #667eea' : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: '8px 8px 0 0'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'all') {
                  e.currentTarget.style.background = '#f7fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'all') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              📚 All Quizzes
              <span style={{
                marginLeft: '8px',
                padding: '2px 8px',
                backgroundColor: activeTab === 'all' ? '#667eea' : '#cbd5e0',
                color: activeTab === 'all' ? 'white' : '#4a5568',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {allCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('passed')}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === 'passed' ? '#48bb78' : '#718096',
                background: activeTab === 'passed' ? '#c6f6d5' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'passed' ? '3px solid #48bb78' : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: '8px 8px 0 0'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'passed') {
                  e.currentTarget.style.background = '#f7fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'passed') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              ✓ Passed
              <span style={{
                marginLeft: '8px',
                padding: '2px 8px',
                backgroundColor: activeTab === 'passed' ? '#48bb78' : '#cbd5e0',
                color: activeTab === 'passed' ? 'white' : '#4a5568',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {passedCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('failed')}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === 'failed' ? '#f56565' : '#718096',
                background: activeTab === 'failed' ? '#fed7d7' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'failed' ? '3px solid #f56565' : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: '8px 8px 0 0'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'failed') {
                  e.currentTarget.style.background = '#f7fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'failed') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              ✗ Failed
              <span style={{
                marginLeft: '8px',
                padding: '2px 8px',
                backgroundColor: activeTab === 'failed' ? '#f56565' : '#cbd5e0',
                color: activeTab === 'failed' ? 'white' : '#4a5568',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {failedCount}
              </span>
            </button>
          </div>
        </div>

        {/* Pagination info below tabs */}
        {pagination.totalPages > 1 && currentPageCount > 0 && (
          <div style={{
            marginTop: '12px',
            marginBottom: '16px',
            padding: '8px 12px',
            background: '#f7fafc',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#718096',
            textAlign: 'center'
          }}>
            Showing <strong style={{ color: '#4a5568' }}>{currentPageCount}</strong> quiz{currentPageCount !== 1 ? 'zes' : ''} on this page
          </div>
        )}

        {Object.keys(submissions).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
            <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
              No quizzes completed yet
            </p>
            <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
              Take your first quiz to see it here!
            </p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/user')}
              style={{ fontSize: '12px' }}
            >
              🚀 Take a Quiz
            </button>
          </div>
        ) : (
          (() => {
            const filteredSubmissions = Object.entries(submissions)
              .filter(([quizId, quizSubmissions]) => {
                const mainSubmission = quizSubmissions[0];
                
                // Filter by tags if selected
                if (selectedTags.length > 0) {
                  if (!mainSubmission.quiz || !mainSubmission.quiz.tags) return false;
                  const quizTagNames = mainSubmission.quiz.tags.map(tag => tag.name || tag);
                  const hasMatchingTag = selectedTags.some(selectedTag => quizTagNames.includes(selectedTag.value));
                  if (!hasMatchingTag) return false;
                }
                
                // Filter by tab (only if results are visible to students)
                if (activeTab !== 'all' && mainSubmission.quiz.showResultsToStudents !== false) {
                  const percentage = ((mainSubmission.score / mainSubmission.totalQuestions) * 100);
                  const passed = percentage >= 50;
                  
                  if (activeTab === 'passed' && !passed) return false;
                  if (activeTab === 'failed' && passed) return false;
                }
                
                return true;
              });

            if (filteredSubmissions.length === 0) {
              // Show tab-specific empty state
              let emptyMessage = '';
              let emptyIcon = '';
              
              if (activeTab === 'passed') {
                emptyIcon = '🎯';
                emptyMessage = 'No passed quizzes yet. Keep trying!';
              } else if (activeTab === 'failed') {
                emptyIcon = '💪';
                emptyMessage = 'Great! No failed quizzes.';
              } else if (selectedTags.length > 0) {
                emptyIcon = '🔍';
                emptyMessage = 'No quizzes match the selected tags.';
              }

              return (
                <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>{emptyIcon}</div>
                  <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
                    {emptyMessage}
                  </p>
                  {activeTab !== 'all' && (
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setActiveTab('all')}
                      style={{ fontSize: '12px', marginTop: '12px' }}
                    >
                      View All Quizzes
                    </button>
                  )}
                </div>
              );
            }

            return (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredSubmissions
              .map(([quizId, quizSubmissions]) => {
              const mainSubmission = quizSubmissions[0]; // First attempt is the main one
              const practiceAttempts = quizSubmissions.slice(1); // Rest are practice
              const percentage = ((mainSubmission.score / mainSubmission.totalQuestions) * 100).toFixed(2);
              const passed = percentage >= 50;

              return (
                <div 
                  key={quizId}
                  style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px',
                    background: '#fafafa',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafafa';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        color: '#2d3748', 
                        fontSize: '16px', 
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {mainSubmission.quiz.title}
                      </h3>
                      {mainSubmission.quiz.description && (
                        <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                          {mainSubmission.quiz.description}
                        </p>
                      )}
                      
                      {/* Tags display */}
                      {mainSubmission.quiz.tags && mainSubmission.quiz.tags.length > 0 && (
                        <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {mainSubmission.quiz.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              style={{
                                padding: '2px 8px',
                                background: 'white',
                                color: '#667eea',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #c7d2fe'
                              }}
                            >
                              🏷️ {tag.name || tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p style={{ color: '#4a5568', fontSize: '12px' }}>
                        📋 Code: <strong>{mainSubmission.quiz.quizCode}</strong>
                        {mainSubmission.quiz.quizType && (
                          <span style={{ 
                            marginLeft: '12px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: '#fef3c7',
                            color: '#d69e2e',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            📝 {mainSubmission.quiz.quizType}
                          </span>
                        )}
                        {practiceAttempts.length > 0 && (
                          <span style={{ marginLeft: '12px', color: '#d69e2e', fontSize: '11px', fontWeight: '600' }}>
                            🔄 {practiceAttempts.length} practice attempt{practiceAttempts.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                      {mainSubmission.quiz.showResultsToStudents !== false ? (
                        <div style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: passed ? '#c6f6d5' : '#fed7d7',
                          textAlign: 'center',
                          minWidth: '80px'
                        }}>
                          <div style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: passed ? '#2f855a' : '#c53030'
                          }}>
                            {percentage}%
                          </div>
                          <div style={{ fontSize: '11px', color: '#4a5568', marginTop: '2px' }}>
                            {passed ? '✓ Passed' : '✗ Failed'}
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#fef3c7',
                          textAlign: 'center',
                          minWidth: '100px',
                          border: '1.5px solid #f59e0b'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#78350f'
                          }}>
                            🔒 Hidden
                          </div>
                          <div style={{ fontSize: '10px', color: '#78350f', marginTop: '2px' }}>
                            Results not visible
                          </div>
                        </div>
                      )}
                      {mainSubmission.quiz.resultsShared && mainSubmission.percentile !== undefined && (
                        <div style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#eef2ff',
                          textAlign: 'center',
                          minWidth: '80px',
                          border: '1.5px solid #667eea'
                        }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#667eea'
                          }}>
                            {mainSubmission.percentile}th
                          </div>
                          <div style={{ fontSize: '10px', color: '#4a5568', marginTop: '2px' }}>
                            Percentile
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    flexWrap: 'wrap',
                    paddingTop: '12px',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '13px',
                    color: '#4a5568'
                  }}>
                    {mainSubmission.quiz.showResultsToStudents !== false && (
                      <span>
                        📊 Score: <strong style={{ color: '#2d3748' }}>{mainSubmission.score}/{mainSubmission.totalQuestions}</strong>
                      </span>
                    )}
                    {mainSubmission.quiz.resultsShared && mainSubmission.totalParticipants && mainSubmission.quiz.showResultsToStudents !== false && (
                      <span>
                        👥 Participants: <strong style={{ color: '#2d3748' }}>{mainSubmission.totalParticipants}</strong>
                      </span>
                    )}
                    <span>
                      📅 Submitted: <strong style={{ color: '#2d3748' }}>
                        {new Date(mainSubmission.submittedAt).toLocaleDateString()}
                      </strong>
                    </span>
                    <span>
                      🕐 {new Date(mainSubmission.submittedAt).toLocaleTimeString()}
                    </span>
                  </div>

                  {/* View Details Button */}
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/my-quizzes/${quizId}/attempts`)}
                      style={{ fontSize: '12px', padding: '8px 16px' }}
                    >
                      ▶ View All Attempts
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={async () => {
                        try {
                          showToast('Initializing retake...', 'info');
                          const response = await submissionAPI.initiateRetake(quizId);
                          navigate(`/quiz/${quizId}?retake=true`, { 
                            state: { 
                              quiz: response.data.quiz,
                              isRetake: true,
                              attemptNumber: response.data.attemptNumber,
                              shuffledOptions: response.data.shuffledOptions
                            } 
                          });
                        } catch (error) {
                          console.error('Error initiating retake:', error);
                          showToast(error.response?.data?.message || 'Failed to initiate retake', 'error');
                        }
                      }}
                      style={{ 
                        fontSize: '12px', 
                        padding: '8px 16px',
                        backgroundColor: 'white',
                        color: '#667eea',
                        border: '1.5px solid #667eea'
                      }}
                    >
                      🔄 Retake Quiz
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
            );
          })()
        )}

      {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              style={{
                padding: '8px 16px',
                backgroundColor: pagination.hasPrevPage ? 'white' : '#f7fafc',
                color: pagination.hasPrevPage ? '#667eea' : '#cbd5e0',
                border: '1.5px solid',
                borderColor: pagination.hasPrevPage ? '#667eea' : '#e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: pageNum === currentPage ? '#667eea' : 'white',
                    color: pageNum === currentPage ? 'white' : '#4a5568',
                    border: '1.5px solid',
                    borderColor: pageNum === currentPage ? '#667eea' : '#e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '40px'
                  }}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              style={{
                padding: '8px 16px',
                backgroundColor: pagination.hasNextPage ? 'white' : '#f7fafc',
                color: pagination.hasNextPage ? '#667eea' : '#cbd5e0',
                border: '1.5px solid',
                borderColor: pagination.hasNextPage ? '#667eea' : '#e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyQuizzes;

